import { Link } from 'react-router-dom';
import { ArrowRight, Plus } from 'lucide-react';
import { T } from '../../i18n/copy';
import { clinicPath } from '../../i18n/paths';
import Reveal from '../Reveal';
import SectionHead from '../SectionHead';
import ImageSlot from '../ImageSlot';

export default function ClinicSection({ lang }) {
  const t = T.clinic;
  return (
    <section id="business-clinic" data-header-theme="midnight" data-testid="clinic-section" className="bg-midnight text-bone">
      <div className="editorial-margins py-10 md:py-10">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <SectionHead tone="dark" eyebrow={t.eyebrow[lang]} index={t.index} />
            <Reveal className="mt-10">
              <h2 className="max-w-xl font-tight font-semibold text-[clamp(1.75rem,2.6vw,2.3rem)] leading-[1.06] tracking-[-0.02em]" data-testid="clinic-section-headline">
                {t.headline[lang]}
              </h2>
            </Reveal>
            <Reveal delay={0.1} className="mt-5 max-w-lg">
              <p className="text-base leading-snug text-bone/85">{t.body[lang]} {t.body2[lang]}</p>
            </Reveal>
            <Reveal delay={0.15} className="mt-8">
              <p className="eyebrow text-bone/70">{t.help[lang]}</p>
              <ul className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {t.helpList[lang].map((li) => (
                  <li key={li} className="flex items-center gap-3 text-base text-bone/90">
                    <ArrowRight className="h-3.5 w-3.5 shrink-0 text-vermilion" strokeWidth={2} />
                    {li}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.2} className="mt-8">
              <Link
                to={clinicPath(lang)}
                data-testid="clinic-section-cta"
                className="group inline-flex items-center gap-3 bg-vermilion px-7 py-4 text-[13px] font-medium tracking-[0.06em] text-bone transition-colors duration-300 hover:bg-[#A8402C]"
              >
                {t.cta[lang]}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
              </Link>
            </Reveal>
          </div>

          <div className="relative lg:col-span-5">
            <Reveal delay={0.1} className="relative">
              <ImageSlot slotKey="clinic" className="max-h-[220px] w-full sm:max-h-[240px] lg:max-h-[380px]" />
              <div className="pointer-events-none absolute inset-0 bg-midnight/25" aria-hidden="true" />
            </Reveal>
          </div>
        </div>

        <div className="mt-8 border-t border-bone/25 pt-5" data-testid="clinic-process">
          <p className="eyebrow text-vermilion">{t.processLabel[lang]}</p>
          <div className="mt-4 hidden gap-x-10 gap-y-8 lg:grid lg:grid-cols-4">
            {t.process[lang].map((p, i) => (
              <Reveal key={p.num} delay={i * 0.08} testid={`clinic-process-step-${i}`}>
                <span className="font-tight text-2xl font-medium text-bone/85">{p.num}</span>
                <h3 className="mt-3 text-base font-medium">{p.title}</h3>
                <p className="mt-2 max-w-[34ch] text-base leading-relaxed text-bone/75">{p.desc}</p>
              </Reveal>
            ))}
          </div>
          <div className="mt-2 lg:hidden">
            {t.process[lang].map((p, i) => (
              <details key={p.num} data-testid={`clinic-process-mobile-${i}`} className="group border-t border-bone/15">
                <summary className="flex cursor-pointer list-none items-center gap-3 py-3.5">
                  <span className="font-tight text-lg font-medium text-vermilion">{p.num}</span>
                  <span className="flex-1 text-base font-medium text-bone">{p.title}</span>
                  <Plus className="h-4 w-4 shrink-0 text-bone/50 transition-transform duration-200 group-open:rotate-45" strokeWidth={1.5} />
                </summary>
                <p className="pb-4 pr-2 text-base leading-relaxed text-bone/75">{p.desc}</p>
              </details>
            ))}
          </div>
        </div>

        <p className="mt-6 border-t border-bone/25 pt-4 font-serif text-base italic text-bone/75" data-testid="clinic-section-note">
          {t.note[lang]}
        </p>
        <p className="mt-2.5 text-[12.5px] leading-relaxed text-bone/55" data-testid="clinic-section-nonguarantee">
          {T.form.nonGuarantee[lang]}
        </p>
      </div>
    </section>
  );
}
